import * as THREE from 'three';
import type { SpawnDirector } from '../ai/SpawnDirector';
import type { DinoActor } from '../dinos/DinoActor';
import type { DayClock } from '../survival/DayClock';
import { CINDER_PEAK, setSkyPeriod } from '../world/Sky';

export type EndingId = 'seal' | 'carry' | 'feed' | 'burn';

export const ENDINGS: readonly EndingId[] = ['seal', 'carry', 'feed', 'burn'];

export const ENDING_LINE: Record<EndingId, string> = {
  seal: 'The Pulse is still.',
  carry: 'The flask left with you.',
  feed: 'The island learned your heartbeat.',
  burn: 'The nursery collapsed.',
};

export const CARRY_SICK_HP = 0.01;
export const CARRY_WANDER_SCALE = 0.4;
export const LAVA_GLOW_BASE = 1.6;
export const LAVA_GLOW_BURN = 3.6;

export type EndingWorld = {
  scene: THREE.Scene;
  fauna: SpawnDirector;
  day: DayClock;
  dinos: () => readonly (DinoActor | null | undefined)[];
};

export function parseEnding(raw: unknown): EndingId | null {
  if (typeof raw !== 'string') {
    return null;
  }
  const id = raw.trim().toLowerCase();
  if (id === 'seal' || id === 'carry' || id === 'feed' || id === 'burn') {
    return id;
  }
  return null;
}

/** World-state after a MATINS verb. Island stays walkable (G16). */
export class Ending {
  id: EndingId | null = null;
  line = '';

  constructor(private readonly world: EndingWorld) {}

  reset(): void {
    this.id = null;
    this.line = '';
  }

  apply(ending: unknown): void {
    const next = parseEnding(ending);
    if (!next || this.id) {
      return;
    }
    this.id = next;
    this.line = ENDING_LINE[next];
    paintLine(this.line);
    if (next === 'seal') {
      this.world.fauna.paused = true;
    } else if (next === 'carry') {
      this.applyCarry();
    } else if (next === 'feed') {
      this.world.day.lockNight = true;
      setSkyPeriod(this.world.scene, true);
    } else {
      this.applyBurn();
    }
  }

  tick(dt: number): void {
    if (this.id === 'carry') {
      this.applyCarry(dt);
      return;
    }
    if (this.id === 'burn') {
      this.forceFlee();
    }
  }

  private applyCarry(dt = 0): void {
    for (const dino of this.liveDinos()) {
      dino.wanderScale = CARRY_WANDER_SCALE;
      if (dt > 0) {
        dino.sicken(dt, CARRY_SICK_HP);
      }
    }
  }

  private applyBurn(): void {
    paintAsh(true);
    boostLavaGlow(this.world.scene, true);
    this.forceFlee();
  }

  private forceFlee(): void {
    for (const dino of this.liveDinos()) {
      dino.forcedFlee = true;
    }
  }

  private liveDinos(): DinoActor[] {
    const out: DinoActor[] = [];
    for (const dino of this.world.dinos()) {
      if (dino?.live) {
        out.push(dino);
      }
    }
    return out;
  }
}

export function boostLavaGlow(scene: THREE.Scene, on: boolean): void {
  const glow = scene.getObjectByName('VZ-LAVA-GLOW');
  if (glow instanceof THREE.PointLight) {
    glow.intensity = on ? LAVA_GLOW_BURN : LAVA_GLOW_BASE;
    glow.distance = on ? 42 : 28;
  }
  scene.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh) || !obj.name.startsWith('VZ-LAVA')) {
      return;
    }
    const mat = obj.material;
    if (mat instanceof THREE.MeshStandardMaterial) {
      mat.emissiveIntensity = on ? 2.4 : 1.15;
    }
  });
  let cinder = scene.getObjectByName('MT-CINDER-GLOW');
  if (on && !(cinder instanceof THREE.PointLight)) {
    const light = new THREE.PointLight(0xff4a10, 4.2, 90, 1.15);
    light.name = 'MT-CINDER-GLOW';
    light.position.set(CINDER_PEAK.x, CINDER_PEAK.y + 6, CINDER_PEAK.z);
    scene.add(light);
    cinder = light;
  }
  if (cinder instanceof THREE.PointLight) {
    cinder.intensity = on ? 4.2 : 0;
    cinder.visible = on;
  }
}

function paintLine(text: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  const el = ensureEl('ending-line', 'Ending');
  el.textContent = text;
  el.hidden = text.length === 0;
}

function paintAsh(on: boolean): void {
  if (typeof document === 'undefined') {
    return;
  }
  const el = ensureEl('hud-ash', 'Ash');
  el.hidden = !on;
  el.setAttribute('aria-hidden', on ? 'false' : 'true');
}

function ensureEl(id: string, label: string): HTMLElement {
  const existing = document.getElementById(id);
  if (existing) {
    return existing;
  }
  const el = document.createElement('div');
  el.id = id;
  el.setAttribute('data-hud', id === 'hud-ash' ? 'ash' : 'ending');
  el.setAttribute('aria-label', label);
  if (id === 'ending-line') {
    el.setAttribute('aria-live', 'polite');
  }
  const host =
    document.getElementById('hud') ?? document.getElementById('ui-root') ?? document.body;
  host.append(el);
  return el;
}
